/**
 * OrganizedMatchesView.js
 *
 * Componente para mostrar partidos organizados con diseño responsive
 * Incluye DataTable para jugadores y estadísticas mejoradas
 * Actualizado: Sin indicador de scroll
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Button, Form, Row, Col, Spinner, Alert, Modal, Table } from 'react-bootstrap';
import { FaFutbol, FaClock, FaUsers, FaTrash, FaExchangeAlt, FaUserTie, FaPlus, FaTimes, FaLock, FaEdit, FaUserPlus, FaBell, FaBellSlash, FaUndo, FaStickyNote, FaCalendarAlt, FaGift, FaInfoCircle, FaMapMarkerAlt } from 'react-icons/fa'; // FaMapMarkerAlt: C-01 multi-campo
import './OrganizedMatchesView.css';
import '../../styles/mobile-datatable-fixes.css';
import entrenadorEnEquipoService from '../../services/entrenadorEnEquipoService';
import futbolistaEnEquipoService from '../../services/futbolistaEnEquipoService';
import jornadaService from '../../services/jornadaService';
import partidoService from '../../services/partidoService';
import EditarHoraPartido from './EditarHoraPartido';
import EditarCampoPartido from './EditarCampoPartido'; // C-01 multi-campo
import AddPlayerModal from './AddPlayerModal';
import { toast } from 'react-toastify';
import { formatFechaJornadaCorta } from '../../utils/fechaUtils';

const OrganizedMatchesView = ({
  partidos,
  noAsignados,
  onPlayerMove: _onPlayerMove,
  onPlayerRemove: _onPlayerRemove,
  onPlayerSubstitute: _onPlayerSubstitute,
  onDataChange,
  onReload,
  loading = false,
  showActions = true,
  compact = false,
  jornadaId,
  sedeId
}) => {
  const [_selectedMatch, setSelectedMatch] = useState(null);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [actionType, setActionType] = useState(''); // 'move', 'remove', 'substitute'

  // Estados para gestión de entrenadores
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [availableCoaches, setAvailableCoaches] = useState([]);
  const [teamCoaches, setTeamCoaches] = useState({});
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  const [assigningCoach, setAssigningCoach] = useState(false);
  const [eliminandoPartido, setEliminandoPartido] = useState(null);
  const [cerrandoPartido, setCerrandoPartido] = useState(null);
  const [revertiendoPartido, setRevertiendoPartido] = useState(null);

  // Estados para editar hora del partido
  const [showEditarHoraModal, setShowEditarHoraModal] = useState(false);
  const [partidoParaEditarHora, setPartidoParaEditarHora] = useState(null);

  // Estados para asignar campo del partido (C-01 multi-campo)
  const [showEditarCampoModal, setShowEditarCampoModal] = useState(false);
  const [partidoParaEditarCampo, setPartidoParaEditarCampo] = useState(null);

  // Estados para añadir futbolista
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [selectedTeamForPlayer, setSelectedTeamForPlayer] = useState(null);

  // Estados para gestión de jugadores
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [jugadoresParaSustituir, setJugadoresParaSustituir] = useState([]);
  const [loadingEquipos, setLoadingEquipos] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  // Estados para notificaciones de horario
  const [notificacionesHorario, setNotificacionesHorario] = useState({});
  const [renotificandoPartido, setRenotificandoPartido] = useState(null);

  // Estados para anotaciones
  const [anotacionesPartido, setAnotacionesPartido] = useState({}); // { partidoId: [anotaciones] }
  const [showAnotacionForm, setShowAnotacionForm] = useState(null); // partidoId o null
  const [nuevaAnotacion, setNuevaAnotacion] = useState({ equipo_id: '', tipo: 'observacion', contenido: '' });
  const [guardandoAnotacion, setGuardandoAnotacion] = useState(false);

  // Estados para añadir jugador a partido confirmado (traer/invitar)
  const [showAddConfirmedModal, setShowAddConfirmedModal] = useState(false);
  const [addConfirmedTarget, setAddConfirmedTarget] = useState(null); // { partidoId, equipoId, equipoNombre }
  const [addConfirmedPlayers, setAddConfirmedPlayers] = useState([]);
  const [addConfirmedAllPlayers, setAddConfirmedAllPlayers] = useState([]);
  const [addConfirmedSearch, setAddConfirmedSearch] = useState('');
  const [addConfirmedLoading, setAddConfirmedLoading] = useState(false);
  const [addConfirmedAdding, setAddConfirmedAdding] = useState(false);
  const [jornadaSeleccionada, setJornadaSeleccionada] = useState({}); // { futbolista_id: jornada_id }

  // Cargar estado de notificaciones cuando cambian los partidos
  useEffect(() => {
    const cargarNotificaciones = async () => {
      if (!partidos || partidos.length === 0) return;

      const notificaciones = {};
      for (const partido of partidos) {
        try {
          const response = await partidoService.getNotificacionesHorarioPartido(partido.partido_id);
          if (response.success) {
            notificaciones[partido.partido_id] = {
              tiene_notificaciones: response.tiene_notificaciones,
              total: response.total_notificaciones,
              ultima: response.ultima_notificacion
            };
          }
        } catch (error) {
          console.error(`Error cargando notificaciones para partido ${partido.partido_id}:`, error);
        }
      }
      setNotificacionesHorario(notificaciones);
    };

    cargarNotificaciones();
  }, [partidos]);

  // Función para abrir modal de acciones de jugador
  const handlePlayerAction = (player, match, action) => {
    // Encontrar el equipo al que pertenece este jugador
    let equipoId = null;
    match.equipos.forEach(equipo => {
      equipo.futbolistas.forEach(futbolista => {
        if (futbolista.futbolista_id === player.futbolista_id) {
          equipoId = equipo.equipo_id;
        }
      });
    });

    setSelectedPlayer({
      ...player,
      matchId: match.partido_id,
      equipo_id: equipoId,
      id: player.futbolista_id // Mapear futbolista_id a id para compatibilidad
    });
    setSelectedMatch(match);
    setActionType(action);

    // Cargar datos específicos según la acción
    if (action === 'move') {
      loadEquiposDisponibles(match.partido_id, equipoId);
    } else if (action === 'substitute') {
      loadJugadoresParaSustituir(match);
    }

    setShowPlayerModal(true);
  };

  // Cargar entrenadores disponibles
  const loadAvailableCoaches = async () => {
    try {
      setLoadingCoaches(true);
      const response = await entrenadorEnEquipoService.getEntrenadoresDisponibles();
      if (response.success) {
        setAvailableCoaches(response.data);
      }
    } catch (error) {
      console.error('Error cargando entrenadores:', error);
      toast.error('Error al cargar entrenadores disponibles');
    } finally {
      setLoadingCoaches(false);
    }
  };

  // Cargar entrenadores asignados a equipos
  const loadTeamCoaches = async () => {
    if (!partidos || partidos.length === 0) return;

    try {
      const coachPromises = [];
      const equipoIds = [];

      partidos.forEach(partido => {
        partido.equipos.forEach(equipo => {
          equipoIds.push(equipo.equipo_id);
          coachPromises.push(
            entrenadorEnEquipoService.getEntrenadoresPorEquipo(equipo.equipo_id)
          );
        });
      });

      const results = await Promise.all(coachPromises);
      const newTeamCoaches = {};

      results.forEach((result, index) => {
        const equipoId = equipoIds[index];
        if (result.success) {
          newTeamCoaches[equipoId] = result.data;
        }
      });

      setTeamCoaches(newTeamCoaches);
    } catch (error) {
      console.error('Error cargando entrenadores de equipos:', error);
    }
  };

  // Abrir modal para asignar entrenador
  const handleAssignCoach = (equipo, partido) => {
    setSelectedTeam({ ...equipo, partidoId: partido.partido_id });
    setShowCoachModal(true);
    if (availableCoaches.length === 0) {
      loadAvailableCoaches();
    }
  };

  // Asignar entrenador a equipo
  const assignCoachToTeam = async (entrenadorId) => {
    if (!selectedTeam || !entrenadorId) return;

    try {
      setAssigningCoach(true);
      const response = await entrenadorEnEquipoService.asignarEntrenadorAEquipo({
        equipo_id: selectedTeam.equipo_id,
        entrenador_id: entrenadorId,
        notas: `Asignado al ${selectedTeam.nombre} - Partido ${selectedTeam.partidoId}`
      });

      if (response.success) {
        toast.success('Entrenador asignado exitosamente');
        setShowCoachModal(false);
        // Recargar entrenadores del equipo
        await loadTeamCoaches();
      }
    } catch (error) {
      console.error('Error asignando entrenador:', error);
      toast.error(error.message || 'Error al asignar entrenador');
    } finally {
      setAssigningCoach(false);
    }
  };

  // Remover entrenador de equipo
  const removeCoachFromTeam = async (asignacionId) => {
    try {
      const response = await entrenadorEnEquipoService.removerEntrenadorDeEquipo(asignacionId);
      if (response.success) {
        toast.success('Entrenador removido exitosamente');
        await loadTeamCoaches();
      }
    } catch (error) {
      console.error('Error removiendo entrenador:', error);
      toast.error(error.message || 'Error al remover entrenador');
    }
  };

  // Cargar entrenadores cuando cambien los partidos
  useEffect(() => {
    if (partidos && partidos.length > 0) {
      loadTeamCoaches();
    }
  }, [partidos]);

  // Función para abrir modal de añadir futbolista (legacy)
  const _handleAddPlayer = (equipo, partido) => {
    setSelectedTeamForPlayer({
      ...equipo,
      partidoId: partido.partido_id,
      jornadaId: jornadaId,
      sedeId: sedeId
    });
    setShowAddPlayerModal(true);
  };

  // Función para manejar cuando se asigna un jugador
  const handlePlayerAdded = (futbolistaId) => {
    console.log('Jugador asignado:', futbolistaId);
    setShowAddPlayerModal(false);
    if (onReload) {
      onReload();
    }
  };

  // === Funciones para añadir jugador a partido confirmado (traer/invitar) ===
  const handleOpenAddConfirmed = useCallback(async (equipo, partido) => {
    setAddConfirmedTarget({
      partidoId: partido.partido_id,
      equipoId: equipo.equipo_id,
      equipoNombre: equipo.nombre || `Equipo ${equipo.numero_equipo}`
    });
    setAddConfirmedSearch('');
    setAddConfirmedPlayers([]);
    setJornadaSeleccionada({});
    setShowAddConfirmedModal(true);
    setAddConfirmedLoading(true);
    try {
      const res = await jornadaService.getFutbolistasParaPartidoConfirmado(partido.partido_id, '');
      const data = res.success ? res.data : [];
      setAddConfirmedAllPlayers(data);
      setAddConfirmedPlayers(data);
    } catch (err) {
      console.error('Error cargando futbolistas:', err);
      toast.error('Error al cargar futbolistas disponibles');
      // Cerrar modal en caso de error para evitar bloqueo de UI
      setShowAddConfirmedModal(false);
      setAddConfirmedTarget(null);
    } finally {
      setAddConfirmedLoading(false);
    }
  }, []);

  const handleSearchConfirmedPlayers = useCallback((term) => {
    setAddConfirmedSearch(term);
    if (!term.trim()) {
      setAddConfirmedPlayers(addConfirmedAllPlayers);
    } else {
      const lower = term.toLowerCase();
      setAddConfirmedPlayers(addConfirmedAllPlayers.filter(f => f.nombre?.toLowerCase().includes(lower)));
    }
  }, [addConfirmedAllPlayers]);

  const closeAddConfirmedModal = useCallback(() => {
    setShowAddConfirmedModal(false);
    setAddConfirmedTarget(null);
    setAddConfirmedPlayers([]);
    setAddConfirmedAllPlayers([]);
    setAddConfirmedSearch('');
    setJornadaSeleccionada({});
  }, []);

  const handleAddConfirmedPlayer = useCallback(async (futbolista, accion) => {
    if (!addConfirmedTarget) return;
    const jornadaOrigenId = accion === 'traer'
      ? (jornadaSeleccionada[futbolista.futbolista_id] || (futbolista.jornadas_futuras?.[0]?.jornada_id ?? null))
      : null;
    setAddConfirmedAdding(true);
    try {
      const res = await jornadaService.añadirJugadorAPartidoConfirmado(addConfirmedTarget.partidoId, {
        futbolista_id: futbolista.futbolista_id,
        equipo_id: addConfirmedTarget.equipoId,
        accion,
        jornada_origen_id: jornadaOrigenId
      });
      if (res.success) {
        toast.success(res.message);
        closeAddConfirmedModal();
        if (onReload) onReload();
      } else {
        toast.error(res.message || 'Error al añadir jugador');
      }
    } catch (err) {
      console.error('Error añadiendo jugador:', err);
      toast.error(err.response?.data?.message || 'Error al añadir jugador');
    } finally {
      setAddConfirmedAdding(false);
    }
  }, [addConfirmedTarget, closeAddConfirmedModal, onReload]);

  // Función para eliminar partido completo
  const handleEliminarPartido = async (partidoId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este partido? Se eliminarán todos los equipos y asignaciones de jugadores. Esta acción no se puede deshacer.')) {
      return;
    }

    setEliminandoPartido(partidoId);

    try {
      const response = await jornadaService.eliminarPartidoCompleto(partidoId);

      if (response.success) {
        toast.success(`Partido eliminado exitosamente. ${response.detalles.jugadores_liberados} jugadores devueltos a no asignados.`);

        // Recargar datos
        if (onReload) {
          onReload();
        }
      } else {
        throw new Error(response.message || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error eliminando partido:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`Error al eliminar partido: ${errorMessage}`);
    } finally {
      setEliminandoPartido(null);
    }
  };

  // Función para revertir partido confirmado a borrador
  const handleRevertirABorrador = async (partidoId) => {
    if (!window.confirm('¿Revertir este partido a borrador? Los jugadores volverán al borrador para editarlos.')) {
      return;
    }

    setRevertiendoPartido(partidoId);

    try {
      const response = await jornadaService.revertirPartidoABorrador(partidoId);

      if (response.success) {
        toast.success(response.message);
        if (onReload) onReload();
      } else {
        throw new Error(response.message || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error revirtiendo partido:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`Error al revertir partido: ${errorMessage}`);
    } finally {
      setRevertiendoPartido(null);
    }
  };

  // Función para cerrar partido
  const handleCerrarPartido = async (partidoId) => {
    if (!window.confirm('¿Estás seguro de que quieres cerrar este partido? Se descontará 1 jornada del saldo de cada futbolista participante. Esta acción no se puede deshacer.')) {
      return;
    }

    setCerrandoPartido(partidoId);

    try {
      const response = await jornadaService.cerrarPartido(partidoId);

      if (response.success) {
        toast.success(`Partido cerrado exitosamente. ${response.detalles.futbolistas_afectados} futbolistas descontados.`);

        // Recargar datos
        if (onReload) {
          onReload();
        }
      } else {
        throw new Error(response.message || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error cerrando partido:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`Error al cerrar partido: ${errorMessage}`);
    } finally {
      setCerrandoPartido(null);
    }
  };

  // Función para abrir modal de editar hora
  const handleEditarHora = (partido) => {
    setPartidoParaEditarHora(partido);
    setShowEditarHoraModal(true);
  };

  // Función para manejar actualización de partido después de editar hora
  const handlePartidoUpdated = (partidoActualizado) => {
    // Actualizar el partido en la lista local
    if (onDataChange) {
      onDataChange(); // Recargar datos desde el padre
    }
    toast.success('Hora del partido actualizada correctamente');

    console.log('🔔 Partido actualizado:', partidoActualizado);

    // Esperar 2 segundos para que las notificaciones se procesen en el backend
    // (se envían de forma asíncrona con setImmediate)
    setTimeout(async () => {
      try {
        console.log('🔔 Consultando notificaciones para partido:', partidoActualizado.partido_id);
        const response = await partidoService.getNotificacionesHorarioPartido(partidoActualizado.partido_id);
        console.log('🔔 Respuesta de notificaciones:', response);
        if (response.success) {
          setNotificacionesHorario(prev => {
            const newState = {
              ...prev,
              [partidoActualizado.partido_id]: {
                tiene_notificaciones: response.tiene_notificaciones,
                total: response.total_notificaciones,
                ultima: response.ultima_notificacion
              }
            };
            console.log('🔔 Nuevo estado de notificaciones:', newState);
            return newState;
          });
        }
      } catch (error) {
        console.error('Error recargando notificaciones:', error);
      }
    }, 2000);
  };

  // Función para cerrar modal de editar hora
  const handleCloseEditarHora = () => {
    setShowEditarHoraModal(false);
    setPartidoParaEditarHora(null);
  };

  // ── C-01 multi-campo: handlers para asignar campo ──────────────────────────
  const handleEditarCampo = (partido) => {
    setPartidoParaEditarCampo(partido);
    setShowEditarCampoModal(true);
  };

  const handleCloseEditarCampo = () => {
    setShowEditarCampoModal(false);
    setPartidoParaEditarCampo(null);
  };

  const handleCampoPartidoUpdated = (_partidoActualizado) => {
    // Propagar el cambio al componente padre para que actualice la lista
    if (onDataChange) onDataChange();
    if (onReload) onReload();
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Función para renotificar solo a futbolistas no notificados
  const handleRenotificarSelectivo = async (partido) => {
    try {
      setRenotificandoPartido(partido.partido_id);

      // Obtener futbolistas no notificados
      const response = await partidoService.getFutbolistasNoNotificados(partido.partido_id);

      if (!response.success) {
        toast.error('Error al obtener futbolistas no notificados');
        return;
      }

      const { total_no_notificados, futbolistas_notificados } = response;

      if (total_no_notificados === 0) {
        toast.info('Todos los futbolistas ya han sido notificados');
        return;
      }

      // Confirmar con el usuario
      const confirmar = window.confirm(
        `¿Deseas notificar a ${total_no_notificados} futbolista(s) que aún no han sido notificados?\n\n` +
        `Total en el partido: ${response.total_futbolistas}\n` +
        `Ya notificados: ${response.total_notificados}\n` +
        `Pendientes: ${total_no_notificados}`
      );

      if (!confirmar) {
        return;
      }

      // Reenviar notificaciones excluyendo los ya notificados
      await partidoService.reenviarNotificacionesSelectivas(
        partido.partido_id,
        futbolistas_notificados
      );

      toast.success(`Notificaciones enviadas a ${total_no_notificados} futbolista(s)`);

      // Recargar estado de notificaciones después de 2 segundos
      setTimeout(async () => {
        try {
          const notifResponse = await partidoService.getNotificacionesHorarioPartido(partido.partido_id);
          if (notifResponse.success) {
            setNotificacionesHorario(prev => ({
              ...prev,
              [partido.partido_id]: {
                tiene_notificaciones: notifResponse.tiene_notificaciones,
                total: notifResponse.total_notificaciones,
                ultima: notifResponse.ultima_notificacion
              }
            }));
          }
        } catch (error) {
          console.error('Error recargando notificaciones:', error);
        }
      }, 2000);

    } catch (error) {
      console.error('Error en renotificación selectiva:', error);
      toast.error('Error al enviar notificaciones selectivas');
    } finally {
      setRenotificandoPartido(null);
    }
  };

  // Cargar equipos disponibles para mover jugador
  const loadEquiposDisponibles = (partidoId, equipoActualId) => {
    setLoadingEquipos(true);
    try {
      // Obtener todos los equipos de todos los partidos organizados
      const equiposDisponibles = [];

      partidos.forEach(partido => {
        partido.equipos.forEach(equipo => {
          if (equipo.equipo_id !== equipoActualId) {
            equiposDisponibles.push({
              ...equipo,
              partido_info: {
                partido_id: partido.partido_id,
                modalidad: partido.modalidad,
                hora: partido.hora
              }
            });
          }
        });
      });

      setEquiposDisponibles(equiposDisponibles);
    } catch (error) {
      console.error('Error al cargar equipos disponibles:', error);
      toast.error('Error al cargar equipos disponibles');
    } finally {
      setLoadingEquipos(false);
    }
  };

  // Cargar jugadores disponibles para sustituir (todos los equipos + no asignados)
  const loadJugadoresParaSustituir = (_match) => {
    setLoadingEquipos(true);
    try {
      const jugadoresDisponibles = [];

      // 1. Obtener jugadores de TODOS los equipos de la jornada (no solo del partido actual)
      if (partidos && partidos.length > 0) {
        partidos.forEach(partido => {
          partido.equipos.forEach(equipo => {
            if (equipo.futbolistas && equipo.futbolistas.length > 0) {
              equipo.futbolistas.forEach(futbolista => {
                // No incluir al jugador seleccionado
                if (futbolista.id !== selectedPlayer?.id) {
                  jugadoresDisponibles.push({
                    ...futbolista,
                    equipo_info: {
                      equipo_id: equipo.equipo_id,
                      nombre: equipo.equipo_nombre,
                      partido_id: partido.partido_id
                    },
                    tipo: 'asignado'
                  });
                }
              });
            }
          });
        });
      }

      // 2. Obtener jugadores no asignados
      if (noAsignados && noAsignados.length > 0) {
        noAsignados.forEach(futbolista => {
          // No incluir al jugador seleccionado si está en no asignados
          if (futbolista.futbolista_id !== selectedPlayer?.id) {
            jugadoresDisponibles.push({
              id: futbolista.futbolista_id,
              nombre: futbolista.nombre,
              ano_nacimiento: futbolista.ano_nacimiento,
              edad: futbolista.edad || (new Date().getFullYear() - futbolista.ano_nacimiento), // Calcular edad si no viene del backend
              equipo_info: {
                equipo_id: null,
                nombre: 'No Asignado',
                partido_id: null
              },
              tipo: 'no_asignado'
            });
          }
        });
      }

      setJugadoresParaSustituir(jugadoresDisponibles);
    } catch (error) {
      console.error('Error al cargar jugadores para sustituir:', error);
      toast.error('Error al cargar jugadores para sustituir');
    } finally {
      setLoadingEquipos(false);
    }
  };

  // Ejecutar acción de mover jugador
  const ejecutarMoverJugador = async (equipoDestinoId) => {
    if (!selectedPlayer || !equipoDestinoId) return;

    setProcessingAction(true);
    try {
      const response = await futbolistaEnEquipoService.moverJugador({
        futbolista_id: selectedPlayer.id,
        equipo_origen_id: selectedPlayer.equipo_id,
        equipo_destino_id: equipoDestinoId
      });

      if (response.success) {
        toast.success(response.message);
        handleCloseModal();
        // Recargar datos sin salir de la página
        if (onDataChange) await onDataChange();
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      console.error('Error al mover jugador:', error);
      toast.error('Error al mover jugador');
    } finally {
      setProcessingAction(false);
    }
  };

  // Ejecutar acción de sustituir jugadores
  const ejecutarSustituirJugadores = async (jugadorDestinoId, equipoDestinoId) => {
    if (!selectedPlayer || !jugadorDestinoId) return;

    setProcessingAction(true);
    try {
      let response;

      // Caso 1: Intercambio con jugador no asignado
      if (!equipoDestinoId) {
        // El jugador seleccionado va a no asignados y el no asignado va al equipo del seleccionado

        // Primero desasignar el jugador actual
        const desasignarResponse = await futbolistaEnEquipoService.desasignarJugador({
          futbolista_id: selectedPlayer.id,
          equipo_id: selectedPlayer.equipo_id
        });

        if (!desasignarResponse.success) {
          throw new Error(desasignarResponse.error);
        }

        // Luego asignar el jugador no asignado al equipo
        const asignarResponse = await futbolistaEnEquipoService.asignarJugador({
          futbolista_id: jugadorDestinoId,
          equipo_id: selectedPlayer.equipo_id,
          es_titular: false
        });

        if (!asignarResponse.success) {
          throw new Error(asignarResponse.error);
        }

        toast.success('Jugadores intercambiados exitosamente');
      }
      // Caso 2: Intercambio entre jugadores de equipos
      else {
        response = await futbolistaEnEquipoService.sustituirJugadores({
          futbolista_1_id: selectedPlayer.id,
          equipo_1_id: selectedPlayer.equipo_id,
          futbolista_2_id: jugadorDestinoId,
          equipo_2_id: equipoDestinoId
        });

        if (response.success) {
          toast.success(response.message);
        } else {
          toast.error(response.error);
        }
      }

      handleCloseModal();
      // Recargar datos sin salir de la página
      if (onDataChange) await onDataChange();
    } catch (error) {
      console.error('Error al sustituir jugadores:', error);
      toast.error('Error al intercambiar jugadores');
    } finally {
      setProcessingAction(false);
    }
  };

  // Ejecutar acción de desasignar jugador
  const ejecutarDesasignarJugador = async () => {
    if (!selectedPlayer) {
      return;
    }

    setProcessingAction(true);
    try {
      const response = await futbolistaEnEquipoService.desasignarJugador({
        futbolista_id: selectedPlayer.id,
        equipo_id: selectedPlayer.equipo_id
      });

      if (response.success) {
        toast.success(response.message);
        handleCloseModal();
        // Recargar datos sin salir de la página
        if (onDataChange) await onDataChange();
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      console.error('Error al desasignar jugador:', error);
      toast.error('Error al desasignar jugador');
    } finally {
      setProcessingAction(false);
    }
  };

  // Función para cerrar modal
  const handleCloseModal = () => {
    setShowPlayerModal(false);
    setSelectedPlayer(null);
    setSelectedMatch(null);
    setActionType('');
    setEquiposDisponibles([]);
    setJugadoresParaSustituir([]);
  };

  // ===== FUNCIONES DE ANOTACIONES =====

  const cargarAnotaciones = async (partidoId) => {
    try {
      const response = await jornadaService.getAnotaciones(partidoId);
      if (response.success) {
        setAnotacionesPartido(prev => ({ ...prev, [partidoId]: response.anotaciones }));
      }
    } catch (error) {
      console.error('Error al cargar anotaciones:', error);
    }
  };

  const handleCrearAnotacion = async (partidoId) => {
    if (!nuevaAnotacion.contenido.trim()) {
      toast.warning('Escribe el contenido de la anotación');
      return;
    }
    setGuardandoAnotacion(true);
    try {
      const response = await jornadaService.crearAnotacion(partidoId, {
        equipo_id: nuevaAnotacion.equipo_id || null,
        tipo: nuevaAnotacion.tipo,
        contenido: nuevaAnotacion.contenido
      });
      if (response.success) {
        toast.success('Anotación creada');
        setNuevaAnotacion({ equipo_id: '', tipo: 'observacion', contenido: '' });
        await cargarAnotaciones(partidoId);
      }
    } catch (error) {
      toast.error('Error al crear la anotación');
    } finally {
      setGuardandoAnotacion(false);
    }
  };

  const handleEliminarAnotacion = async (anotacionId, partidoId) => {
    if (!window.confirm('¿Eliminar esta anotación?')) return;
    try {
      const response = await jornadaService.eliminarAnotacion(anotacionId);
      if (response.success) {
        toast.success('Anotación eliminada');
        await cargarAnotaciones(partidoId);
      }
    } catch (error) {
      toast.error('Error al eliminar la anotación');
    }
  };

  const toggleAnotacionForm = (partidoId) => {
    if (showAnotacionForm === partidoId) {
      setShowAnotacionForm(null);
    } else {
      setShowAnotacionForm(partidoId);
      setNuevaAnotacion({ equipo_id: '', tipo: 'observacion', contenido: '' });
      if (!anotacionesPartido[partidoId]) {
        cargarAnotaciones(partidoId);
      }
    }
  };

  const tipoAnotacionConfig = {
    instruccion: { label: 'Instrucción', icon: '📌', color: '#0d6efd' },
    observacion: { label: 'Observación', icon: '👁', color: '#6c757d' },
    cambio: { label: 'Cambio', icon: '🔄', color: '#fd7e14' }
  };

  // Renderizar estadísticas del equipo
  const renderTeamStats = (equipo) => (
    <div className="team-stats-grid">
      <div className="stat-item">
        <div className="stat-label">Jugadores</div>
        <div className="stat-value">{equipo.total_futbolistas}</div>
      </div>
      <div className="stat-item">
        <div className="stat-label">Potencial</div>
        <div className="stat-value">
          {equipo.potencial_promedio ? parseFloat(equipo.potencial_promedio).toFixed(1) : 'N/A'}
        </div>
      </div>
      <div className="stat-item">
        <div className="stat-label">Edad Media</div>
        <div className="stat-value">
          {equipo.edad_promedio ? parseFloat(equipo.edad_promedio).toFixed(1) : 'N/A'}
        </div>
      </div>
      <div className="stat-item">
        <div className="stat-label">Titulares</div>
        <div className="stat-value">
          {equipo.futbolistas.filter(f => f.es_titular).length}
        </div>
      </div>
    </div>
  );

  // Renderizar sección de entrenadores del equipo
  const renderTeamCoaches = (equipo, partido) => {
    const coaches = teamCoaches[equipo.equipo_id] || [];

    return (
      <div className="team-coaches-section mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0 d-flex align-items-center">
            <FaUserTie className="me-2 text-primary" />
            Entrenadores
          </h6>
          {showActions && coaches.length === 0 && (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => handleAssignCoach(equipo, partido)}
              className="d-flex align-items-center"
              disabled={partido.estado === 'confirmado'}
              title={partido.estado === 'confirmado' ? 'No se puede asignar entrenadores a un partido cerrado' : 'Asignar entrenador'}
            >
              <FaPlus className="me-1" />
              Asignar
            </Button>
          )}
        </div>

        {coaches.length > 0 ? (
          <div className="coaches-list">
            {coaches.map((asignacion) => (
              <div key={asignacion.id} className="coach-item d-flex align-items-center justify-content-between mb-2">
                <div className="coach-info d-flex align-items-center">
                  <div className="coach-badge bg-primary text-white rounded px-3 py-2 d-flex align-items-center me-2">
                    <FaUserTie className="me-2" style={{ color: '#ffffff' }} />
                    <strong style={{ color: '#ffffff', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                      {asignacion.entrenador?.nombre || 'Sin nombre'}
                    </strong>
                  </div>
                  {asignacion.notas && (
                    <small className="text-muted d-block">
                      {asignacion.notas}
                    </small>
                  )}
                </div>
                {showActions && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeCoachFromTeam(asignacion.id)}
                    className="ms-2"
                  >
                    <FaTimes />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-coaches text-muted">
            <small>Sin entrenador asignado</small>
          </div>
        )}
      </div>
    );
  };

  // Renderizar vista de tarjetas móviles para jugadores
  const renderPlayersMobileCards = (futbolistas, equipo, partido) => (
    <div className="players-mobile-cards">
      {futbolistas.map((futbolista) => (
        <div
          key={`${partido.partido_id}-${equipo.equipo_id}-${futbolista.futbolista_id}`}
          className={`player-mobile-card ${futbolista.es_titular ? 'titular' : ''}`}
        >
          <div className="player-mobile-header">
            <div>
              <div className="player-mobile-name">
                {futbolista.nombre}
                {futbolista.es_titular && <Badge bg="primary" className="ms-2">Titular</Badge>}
              </div>
              <div className="player-mobile-position text-muted">
                {/* Posición asignada en negrita */}
                <strong>{futbolista.posicion_asignada_nombre || futbolista.posicion_principal_nombre || 'N/A'}</strong>
                {/* Posiciones naturales del jugador en texto más claro y pequeño */}
                <div style={{ fontSize: '10px', color: '#8c8c8c', marginTop: '1px' }}>
                  {futbolista.posicion_principal_nombre && (
                    <span title={`Posición principal: ${futbolista.posicion_principal_nombre}`}>
                      {futbolista.posicion_principal_nombre}
                    </span>
                  )}
                  {futbolista.posicion_secundaria_nombre && futbolista.posicion_secundaria_nombre !== futbolista.posicion_principal_nombre && (
                    <span title={`Posición secundaria: ${futbolista.posicion_secundaria_nombre}`}>
                      {futbolista.posicion_principal_nombre ? ' / ' : ''}{futbolista.posicion_secundaria_nombre}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="player-mobile-stats">
              <small className="text-muted">
                {futbolista.fecha_nacimiento ? new Date(futbolista.fecha_nacimiento).getFullYear() : futbolista.edad || 'N/A'} • Pot: {futbolista.potencial || 'N/A'}
              </small>
            </div>
          </div>

          {showActions && (
            <div className="player-mobile-actions">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handlePlayerAction(futbolista, partido, 'move')}
                className="me-2"
                disabled={partido.estado === 'confirmado'}
                title={partido.estado === 'confirmado' ? 'No se puede mover jugadores de un partido cerrado' : 'Mover jugador'}
              >
                <FaExchangeAlt className="me-1" />
                Mover
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                onClick={() => handlePlayerAction(futbolista, partido, 'substitute')}
                className="me-2"
                disabled={partido.estado === 'confirmado'}
                title={partido.estado === 'confirmado' ? 'No se puede sustituir jugadores de un partido cerrado' : 'Sustituir jugador'}
              >
                <FaUsers className="me-1" />
                Sustituir
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handlePlayerAction(futbolista, partido, 'remove')}
                disabled={partido.estado === 'confirmado'}
                title={partido.estado === 'confirmado' ? 'No se puede quitar jugadores de un partido cerrado' : 'Quitar jugador'}
              >
                <FaTrash className="me-1" />
                Quitar
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Renderizar tabla de jugadores con scroll horizontal mejorado
  const renderPlayersTable = (futbolistas, equipo, partido) => (
    <div className="players-table-container">
      {/* Vista de tabla para desktop */}
      <div className="players-table-wrapper">
        <table className="table table-striped table-sm mb-0 players-table">
          <thead>
            <tr>
              <th>Jugador</th>
              <th>Año Nac.</th>
              <th>Posición</th>
              <th>Potencial</th>
              {showActions && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {futbolistas.map((futbolista) => (
              <tr key={`${partido.partido_id}-${equipo.equipo_id}-${futbolista.futbolista_id}`} className={futbolista.es_titular ? 'titular' : ''}>
                <td className="player-name">
                  {futbolista.nombre}
                  {futbolista.es_titular && <Badge bg="primary" className="ms-1">T</Badge>}
                </td>
                <td>{futbolista.fecha_nacimiento ? new Date(futbolista.fecha_nacimiento).getFullYear() : futbolista.edad || 'N/A'}</td>
                <td>
                  <div>
                    {/* Posición asignada en negrita */}
                    <strong>{futbolista.posicion_asignada_nombre || futbolista.posicion_principal_nombre || 'N/A'}</strong>
                    {/* Posiciones naturales del jugador en texto más claro y pequeño */}
                    <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                      {futbolista.posicion_principal_nombre && (
                        <span title={`Posición principal: ${futbolista.posicion_principal_nombre}`}>
                          {futbolista.posicion_principal_nombre}
                        </span>
                      )}
                      {futbolista.posicion_secundaria_nombre && futbolista.posicion_secundaria_nombre !== futbolista.posicion_principal_nombre && (
                        <span title={`Posición secundaria: ${futbolista.posicion_secundaria_nombre}`}>
                          {futbolista.posicion_principal_nombre ? ' / ' : ''}{futbolista.posicion_secundaria_nombre}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  {futbolista.potencial || 'N/A'}
                </td>
                {showActions && (
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-outline-primary btn-sm action-btn"
                        onClick={() => handlePlayerAction(futbolista, partido, 'move')}
                        title={partido.estado === 'confirmado' ? 'No se puede mover jugadores de un partido cerrado' : 'Mover'}
                        disabled={partido.estado === 'confirmado'}
                      >
                        <FaExchangeAlt />
                      </button>
                      <button
                        className="btn btn-outline-warning btn-sm action-btn"
                        onClick={() => handlePlayerAction(futbolista, partido, 'substitute')}
                        title={partido.estado === 'confirmado' ? 'No se puede sustituir jugadores de un partido cerrado' : 'Sustituir'}
                        disabled={partido.estado === 'confirmado'}
                      >
                        <FaUsers />
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm action-btn"
                        onClick={() => handlePlayerAction(futbolista, partido, 'remove')}
                        title={partido.estado === 'confirmado' ? 'No se puede quitar jugadores de un partido cerrado' : 'Quitar'}
                        disabled={partido.estado === 'confirmado'}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Vista de tarjetas para móviles */}
      {renderPlayersMobileCards(futbolistas, equipo, partido)}

      {/* Botón para añadir futbolista a partido confirmado */}
      {showActions && (
        <div className="mt-2 text-center">
          <Button
            size="sm"
            variant="outline-success"
            onClick={() => handleOpenAddConfirmed(equipo, partido)}
            className="d-flex align-items-center mx-auto"
            style={{ fontSize: '11px', padding: '2px 10px' }}
          >
            <FaUserPlus className="me-1" />
            Añadir Jugador
          </Button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2 text-muted">Cargando partidos organizados...</p>
      </div>
    );
  }

  if (!partidos || partidos.length === 0) {
    return (
      <div className="text-center py-5">
        <FaFutbol className="text-muted mb-3" size={48} />
        <h5 className="text-muted">No hay partidos organizados</h5>
        <p className="text-muted">Los partidos aparecerán aquí una vez organizados.</p>
      </div>
    );
  }

  return (
    <div className={`organized-matches-container ${compact ? 'compact' : ''}`}>
      {partidos.map((partido, index) => (
        <Card key={partido.partido_id} className="match-card mb-4">
          {/* Header del partido */}
          <Card.Header className="match-header">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="match-number">
                  {index + 1}
                </div>
                <div className="match-info">
                  <h6 className="mb-1">Partido {index + 1}</h6>
                  <div className="match-details">
                    <span className="detail-item d-flex align-items-center">
                      <FaClock className="me-1" />
                      {partido.hora || 'Sin hora'}
                      {showActions && (
                        <>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="ms-2 p-1"
                            style={{ fontSize: '10px', lineHeight: '1' }}
                            onClick={() => handleEditarHora(partido)}
                            title="Editar hora del partido"
                          >
                            <FaEdit size={10} />
                          </Button>
                          {/* C-01 multi-campo: botón asignar instalación */}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-1 p-1"
                            style={{ fontSize: '10px', lineHeight: '1' }}
                            onClick={() => handleEditarCampo(partido)}
                            title={partido.campo_nombre
                              ? `Campo: ${partido.campo_nombre} — click para cambiar`
                              : 'Asignar instalación al partido'}
                          >
                            <FaMapMarkerAlt size={10} />
                          </Button>
                          {partido.campo_nombre && (
                            <span
                              className="ms-1"
                              style={{ fontSize: '10px', color: '#0d6efd', fontWeight: '600' }}
                              title={partido.campo_direccion || ''}
                            >
                              📍 {partido.campo_nombre}
                            </span>
                          )}
                          {notificacionesHorario[partido.partido_id]?.tiene_notificaciones ? (
                            <>
                              <span
                                className="ms-2"
                                title={`Notificado ${notificacionesHorario[partido.partido_id].total} vez/veces. Última: ${notificacionesHorario[partido.partido_id].ultima?.fecha ? new Date(notificacionesHorario[partido.partido_id].ultima.fecha).toLocaleString('es-ES') : 'N/A'}`}
                                style={{ color: '#28a745', fontSize: '14px' }}
                              >
                                <FaBell />
                              </span>
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="ms-2 p-1"
                                style={{ fontSize: '10px', lineHeight: '1' }}
                                onClick={() => handleRenotificarSelectivo(partido)}
                                disabled={renotificandoPartido === partido.partido_id}
                                title="Notificar solo a futbolistas no notificados previamente"
                              >
                                {renotificandoPartido === partido.partido_id ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                  <>📧</>
                                )}
                              </Button>
                            </>
                          ) : (
                            <span
                              className="ms-2"
                              title="No se han enviado notificaciones de horario"
                              style={{ color: '#6c757d', fontSize: '14px' }}
                            >
                              <FaBellSlash />
                            </span>
                          )}
                        </>
                      )}
                    </span>
                    <span className="detail-item">
                      Modalidad: F{partido.modalidad === 1 ? '8' : '11'}
                    </span>
                    <Badge
                      bg={partido.estado === 'preorganizado' ? 'warning' : partido.estado === 'confirmado' ? 'success' : 'secondary'}
                      className="status-badge"
                    >
                      {partido.estado === 'preorganizado' ? 'Preorganizado' :
                       partido.estado === 'confirmado' ? 'Cerrado' :
                       partido.estado}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="match-actions">
                {partido.estado === 'preorganizado' && (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    className="me-2"
                    onClick={() => handleCerrarPartido(partido.partido_id)}
                    disabled={cerrandoPartido === partido.partido_id}
                    title="Cerrar partido - Descontar saldo de jornadas"
                  >
                    {cerrandoPartido === partido.partido_id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaLock />
                    )}
                  </Button>
                )}
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => handleRevertirABorrador(partido.partido_id)}
                  disabled={revertiendoPartido === partido.partido_id}
                  title="Revertir a borrador"
                  className="me-1"
                >
                  {revertiendoPartido === partido.partido_id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <><FaUndo className="me-1" /><span style={{ fontSize: '11px' }}>Borrador</span></>
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleEliminarPartido(partido.partido_id)}
                  disabled={eliminandoPartido === partido.partido_id}
                  title="Eliminar partido completo"
                >
                  {eliminandoPartido === partido.partido_id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaTrash />
                  )}
                </Button>
              </div>
            </div>
          </Card.Header>

          {/* Cuerpo del partido - Equipos */}
          <Card.Body className="match-body">
            <Row className="g-4">
              {partido.equipos.map((equipo, equipoIndex) => (
                <Col key={equipo.equipo_id} xl={6} lg={12} md={12} className="team-column">
                  <Card className="team-card h-100">
                    <Card.Header className={`team-header team-${equipoIndex + 1}`}>
                      <div className="d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 team-name">{equipo.nombre}</h6>
                        <Badge bg="secondary" text="white" className="player-count">
                          {equipo.total_futbolistas}
                        </Badge>
                      </div>
                    </Card.Header>
                    
                    <Card.Body className="team-body">
                      {/* Estadísticas del equipo */}
                      <div className="mb-3">
                        {renderTeamStats(equipo)}
                      </div>

                      {/* Sección de entrenadores */}
                      {renderTeamCoaches(equipo, partido)}

                      {/* Tabla de jugadores */}
                      {renderPlayersTable(equipo.futbolistas, equipo, partido)}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Sección de Anotaciones */}
            <div className="mt-3">
              <Button
                variant={showAnotacionForm === partido.partido_id ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => toggleAnotacionForm(partido.partido_id)}
              >
                <FaStickyNote className="me-1" />
                📝 Anotaciones {anotacionesPartido[partido.partido_id]?.length > 0 && (
                  <Badge bg="warning" text="dark" className="ms-1">{anotacionesPartido[partido.partido_id].length}</Badge>
                )}
              </Button>

              {showAnotacionForm === partido.partido_id && (
                <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  {/* Formulario nueva anotación */}
                  <h6 className="mb-3" style={{ color: '#061156' }}>➕ Nueva anotación</h6>
                  <Row className="g-2 mb-2">
                    <Col md={4}>
                      <Form.Select
                        size="sm"
                        value={nuevaAnotacion.equipo_id}
                        onChange={(e) => setNuevaAnotacion(prev => ({ ...prev, equipo_id: e.target.value }))}
                      >
                        <option value="">🔵 General (todos ven)</option>
                        {partido.equipos.map(eq => (
                          <option key={eq.equipo_id} value={eq.equipo_id}>
                            🟢 {eq.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        size="sm"
                        value={nuevaAnotacion.tipo}
                        onChange={(e) => setNuevaAnotacion(prev => ({ ...prev, tipo: e.target.value }))}
                      >
                        <option value="observacion">👁 Observación</option>
                        <option value="instruccion">📌 Instrucción</option>
                        <option value="cambio">🔄 Cambio</option>
                      </Form.Select>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-2">
                    <Col>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        placeholder="Escribe la anotación..."
                        value={nuevaAnotacion.contenido}
                        onChange={(e) => setNuevaAnotacion(prev => ({ ...prev, contenido: e.target.value }))}
                      />
                    </Col>
                  </Row>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleCrearAnotacion(partido.partido_id)}
                    disabled={guardandoAnotacion || !nuevaAnotacion.contenido.trim()}
                  >
                    {guardandoAnotacion ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                  </Button>

                  {/* Lista de anotaciones existentes */}
                  {anotacionesPartido[partido.partido_id]?.length > 0 && (
                    <div className="mt-3">
                      <h6 style={{ color: '#061156', fontSize: '14px' }}>📋 Anotaciones existentes</h6>
                      {anotacionesPartido[partido.partido_id].map(anot => {
                        const config = tipoAnotacionConfig[anot.tipo] || tipoAnotacionConfig.observacion;
                        return (
                          <div
                            key={anot.id}
                            className="d-flex align-items-start justify-content-between p-2 mb-2"
                            style={{
                              backgroundColor: '#fff',
                              borderRadius: '6px',
                              borderLeft: `4px solid ${config.color}`,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div className="d-flex align-items-center mb-1">
                                <span className="me-1">{config.icon}</span>
                                <strong style={{ fontSize: '12px', color: config.color }}>{config.label}</strong>
                                {anot.equipo_nombre ? (
                                  <Badge bg="info" className="ms-2" style={{ fontSize: '10px' }}>{anot.equipo_nombre}</Badge>
                                ) : (
                                  <Badge bg="secondary" className="ms-2" style={{ fontSize: '10px' }}>General</Badge>
                                )}
                                <small className="text-muted ms-2" style={{ fontSize: '10px' }}>
                                  {new Date(anot.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </small>
                              </div>
                              <p className="mb-0" style={{ fontSize: '13px' }}>{anot.contenido}</p>
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0 ms-2"
                              onClick={() => handleEliminarAnotacion(anot.id, partido.partido_id)}
                              title="Eliminar"
                            >
                              <FaTimes />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {anotacionesPartido[partido.partido_id]?.length === 0 && (
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: '13px' }}>No hay anotaciones para este partido.</p>
                  )}
                </div>
              )}
            </div>

          </Card.Body>
        </Card>
      ))}

      {/* Modal para acciones de jugador */}
      {showPlayerModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem',
              width: window.innerWidth <= 768 ? '100vw' : '90vw',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '600px',
              height: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '1rem',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8f9fa'
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 'bold' }}>
                {actionType === 'move' && 'Mover Jugador'}
                {actionType === 'substitute' && 'Sustituir Jugador'}
                {actionType === 'remove' && 'Quitar Jugador'}
              </h5>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0',
                  color: '#6c757d'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div
              style={{
                padding: '1rem',
                flex: 1,
                overflowY: 'auto'
              }}
            >
              {selectedPlayer && (
                <div>
                  <p><strong>Jugador:</strong> {selectedPlayer.nombre}</p>
                  <p><strong>Posición:</strong> {selectedPlayer.posicion_asignada}</p>
                  <p><strong>Edad:</strong> {selectedPlayer.edad} años</p>

                  {actionType === 'move' && (
                    <Form>
                      <Form.Group>
                        <Form.Label>Seleccionar equipo destino:</Form.Label>
                        {loadingEquipos ? (
                          <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">Cargando equipos...</span>
                          </div>
                        ) : (
                          <Form.Select
                            onChange={(e) => setSelectedTeam({ equipoId: e.target.value })}
                            disabled={processingAction}
                          >
                            <option value="">Seleccionar equipo...</option>
                            {equiposDisponibles.map((equipo) => (
                              <option key={equipo.equipo_id} value={equipo.equipo_id}>
                                {equipo.nombre} - Partido {equipo.partido_info.partido_id}
                                (F{equipo.partido_info.modalidad === 1 ? '8' : '11'})
                                {equipo.partido_info.hora && ` - ${equipo.partido_info.hora}`}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </Form.Group>
                    </Form>
                  )}

                  {actionType === 'substitute' && (
                    <Form>
                      <Form.Group>
                        <Form.Label>Seleccionar jugador para intercambiar:</Form.Label>
                        {loadingEquipos ? (
                          <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">Cargando jugadores...</span>
                          </div>
                        ) : (
                          <Form.Select
                            onChange={(e) => {
                              const [jugadorId, equipoId] = e.target.value.split('|');
                              setSelectedTeam({
                                jugadorId: parseInt(jugadorId),
                                equipoId: equipoId === 'null' ? null : parseInt(equipoId)
                              });
                            }}
                            disabled={processingAction}
                          >
                            <option value="">Seleccionar jugador...</option>

                            {/* Agrupar jugadores por tipo */}
                            {jugadoresParaSustituir.filter(j => j.tipo === 'no_asignado').length > 0 && (
                              <optgroup label="🔄 Jugadores No Asignados">
                                {jugadoresParaSustituir
                                  .filter(jugador => jugador.tipo === 'no_asignado')
                                  .map((jugador) => (
                                    <option
                                      key={`no-asignado-${jugador.id}`}
                                      value={`${jugador.id}|null`}
                                    >
                                      {jugador.nombre} ({jugador.ano_nacimiento})
                                    </option>
                                  ))}
                              </optgroup>
                            )}

                            {jugadoresParaSustituir.filter(j => j.tipo === 'asignado').length > 0 && (
                              <optgroup label="⚽ Jugadores en Equipos">
                                {jugadoresParaSustituir
                                  .filter(jugador => jugador.tipo === 'asignado')
                                  .map((jugador) => (
                                    <option
                                      key={`asignado-${jugador.id}`}
                                      value={`${jugador.id}|${jugador.equipo_info.equipo_id}`}
                                    >
                                      {jugador.nombre} - {jugador.equipo_info.nombre}
                                      {jugador.posicion_asignada_nombre && ` (${jugador.posicion_asignada_nombre})`}
                                    </option>
                                  ))}
                              </optgroup>
                            )}
                          </Form.Select>
                        )}
                        <Form.Text className="text-muted">
                          Puedes intercambiar con jugadores de otros equipos o con jugadores no asignados.
                        </Form.Text>
                      </Form.Group>
                    </Form>
                  )}

                  {actionType === 'remove' && (
                    <div className="alert alert-warning">
                      <strong>¿Estás seguro?</strong> Esta acción desasignará al jugador del equipo.
                      <br />
                      El jugador aparecerá en la bolsa de jugadores no asignados y podrá ser reasignado posteriormente.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '1rem',
                borderTop: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.5rem'
              }}
            >
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button
                variant={actionType === 'remove' ? 'danger' : 'primary'}
                disabled={processingAction || (actionType === 'move' && !selectedTeam?.equipoId) || (actionType === 'substitute' && !selectedTeam?.jugadorId)}
                onClick={() => {
                  if (actionType === 'move' && selectedTeam?.equipoId) {
                    ejecutarMoverJugador(parseInt(selectedTeam.equipoId));
                  } else if (actionType === 'substitute' && selectedTeam?.jugadorId) {
                    // Para sustitución, equipoId puede ser null (jugador no asignado)
                    const equipoId = selectedTeam.equipoId ? parseInt(selectedTeam.equipoId) : null;
                    ejecutarSustituirJugadores(parseInt(selectedTeam.jugadorId), equipoId);
                  } else if (actionType === 'remove') {
                    ejecutarDesasignarJugador();
                  }
                }}
              >
                {processingAction ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Procesando...
                  </>
                ) : (
                  <>
                    {actionType === 'move' && 'Mover Jugador'}
                    {actionType === 'substitute' && 'Intercambiar Jugadores'}
                    {actionType === 'remove' && 'Desasignar Jugador'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para asignar entrenador */}
      {showCoachModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCoachModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem',
              width: window.innerWidth <= 768 ? '100vw' : '90vw',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '500px',
              height: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '1rem',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#f8f9fa'
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 600 }}>
                Asignar Entrenador - {selectedTeam?.nombre}
              </h5>
              <button
                onClick={() => setShowCoachModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div
              style={{
                padding: '1rem',
                flex: 1,
                overflow: 'auto'
              }}
            >
              {loadingCoaches ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando entrenadores...
                </div>
              ) : availableCoaches.length > 0 ? (
                <div>
                  <p className="mb-3">Selecciona un entrenador para asignar al equipo:</p>
                  <div className="coaches-grid">
                    {availableCoaches.map((entrenador) => (
                      <div
                        key={entrenador.id}
                        className="coach-option p-3 border rounded mb-2"
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: '#f8f9fa'
                        }}
                        onClick={() => assignCoachToTeam(entrenador.id)}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#e9ecef';
                          e.target.style.borderColor = '#007bff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.borderColor = '#dee2e6';
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <FaUserTie className="me-3 text-primary" size={24} />
                          <div>
                            <h6 className="mb-1">{entrenador.nombre}</h6>
                            {entrenador.usuarioInfo && (
                              <small className="text-muted">
                                Usuario: {entrenador.usuarioInfo.nombre}
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Alert variant="info">
                  <FaUserTie className="me-2" />
                  No hay entrenadores disponibles para asignar.
                </Alert>
              )}

              {assigningCoach && (
                <div className="text-center mt-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Asignando entrenador...
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '1rem',
                borderTop: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowCoachModal(false)}
                disabled={assigningCoach}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar hora del partido */}
      <EditarHoraPartido
        show={showEditarHoraModal}
        onHide={handleCloseEditarHora}
        partido={partidoParaEditarHora}
        onPartidoUpdated={handlePartidoUpdated}
      />

      {/* Modal para asignar instalación (campo) al partido — C-01 multi-campo */}
      <EditarCampoPartido
        show={showEditarCampoModal}
        onHide={handleCloseEditarCampo}
        partido={partidoParaEditarCampo}
        onPartidoUpdated={handleCampoPartidoUpdated}
      />

      {/* Modal para añadir futbolista (legacy) */}
      <AddPlayerModal
        show={showAddPlayerModal}
        onHide={() => setShowAddPlayerModal(false)}
        selectedTeam={selectedTeamForPlayer}
        onPlayerAdded={handlePlayerAdded}
      />

      {/* Modal para añadir jugador a partido confirmado (traer/invitar) */}
      <Modal show={showAddConfirmedModal} onHide={closeAddConfirmedModal} centered size="lg">
        <Modal.Header closeButton className="bg-success text-white py-2" closeVariant="white">
          <Modal.Title style={{ fontSize: '16px' }}>
            <FaUserPlus className="me-2" />Añadir Jugador - {addConfirmedTarget?.equipoNombre}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3 align-items-center">
            <Form.Control
              type="text"
              placeholder="Filtrar por nombre..."
              value={addConfirmedSearch}
              onChange={(e) => handleSearchConfirmedPlayers(e.target.value)}
              autoFocus
              size="sm"
            />
            {addConfirmedLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <span className="text-muted small text-nowrap">{addConfirmedPlayers.length} de {addConfirmedAllPlayers.length}</span>
            )}
          </div>

          {addConfirmedPlayers.length > 0 && (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                <thead>
                  <tr className="text-muted" style={{ fontSize: '11px', backgroundColor: '#f8f9fa' }}>
                    <th>JUGADOR</th>
                    <th className="text-center">SALDO</th>
                    <th className="text-center">JORNADAS FUTURAS</th>
                    <th className="text-center">ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {addConfirmedPlayers.map((f) => (
                    <tr key={f.futbolista_id} style={{ verticalAlign: 'middle' }}>
                      <td>
                        <span className="fw-bold">{f.nombre}</span>
                        <span className="text-muted ms-1">({f.ano_nacimiento || '?'})</span>
                        {f.sin_inscripcion && <span className="badge bg-danger ms-1" style={{ fontSize: '9px' }}>Sin inscripción</span>}
                      </td>
                      <td className="text-center">
                        <span className={`badge ${f.saldo_jornadas > 0 ? 'bg-success' : 'bg-secondary'}`}>
                          {f.saldo_jornadas}
                        </span>
                      </td>
                      <td className="text-center">
                        {f.jornadas_futuras && f.jornadas_futuras.length > 0 ? (
                          <span className="badge bg-info">{f.jornadas_futuras.length} jornada(s)</span>
                        ) : (
                          <span className="text-muted small">Ninguna</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex flex-column gap-1 align-items-center">
                          {!f.sin_inscripcion && f.jornadas_futuras && f.jornadas_futuras.length > 0 && (
                            <div className="d-flex gap-1 align-items-center">
                              {f.jornadas_futuras.length > 1 && (
                                <Form.Select
                                  size="sm"
                                  style={{ fontSize: '10px', padding: '1px 2px', width: '110px' }}
                                  value={jornadaSeleccionada[f.futbolista_id] || f.jornadas_futuras[0].jornada_id}
                                  onChange={(e) => setJornadaSeleccionada(prev => ({ ...prev, [f.futbolista_id]: parseInt(e.target.value) }))}
                                >
                                  {f.jornadas_futuras.map(j => (
                                    <option key={j.jornada_id} value={j.jornada_id}>
                                      J{j.jornada_numero} ({formatFechaJornadaCorta(j.jornada_fecha)})
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                              <Button
                                variant="warning"
                                size="sm"
                                style={{ fontSize: '10px', padding: '2px 6px' }}
                                disabled={addConfirmedAdding}
                                onClick={() => handleAddConfirmedPlayer(f, 'traer')}
                                title="Traer de jornada futura (mover inscripción)"
                              >
                                <FaCalendarAlt className="me-1" />Traer
                              </Button>
                            </div>
                          )}
                          <Button
                            variant="outline-success"
                            size="sm"
                            style={{ fontSize: '10px', padding: '2px 6px' }}
                            disabled={addConfirmedAdding}
                            onClick={() => handleAddConfirmedPlayer(f, 'invitar')}
                            title={f.sin_inscripcion ? "Invitar (se creará inscripción automática, equipación no pagada)" : "Invitar a esta jornada (sin coste)"}
                          >
                            <FaGift className="me-1" />Invitar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {!addConfirmedLoading && addConfirmedPlayers.length === 0 && addConfirmedAllPlayers.length === 0 && (
            <Alert variant="info" className="small py-2 mb-0">
              <FaInfoCircle className="me-1" />No hay futbolistas disponibles con inscripción activa en este torneo.
            </Alert>
          )}

          {!addConfirmedLoading && addConfirmedPlayers.length === 0 && addConfirmedAllPlayers.length > 0 && addConfirmedSearch.length > 0 && (
            <Alert variant="warning" className="small py-2 mb-0">
              <FaInfoCircle className="me-1" />No se encontraron futbolistas para &quot;{addConfirmedSearch}&quot;.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={closeAddConfirmedModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrganizedMatchesView;
